import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { randomUUID } from '@/utils/uuid';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { z } from 'zod';

import { AnalisisDatosGeneralesForm } from '@/features/analisis/common/components/AnalisisDatosGeneralesForm';
import type { AnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { useAnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { formatNumeroAnalisis } from '@/features/analisis/utils/formatNumeroAnalisis';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';
import { TipoAnalisis } from '@/types/enums';

import { useBaermann, useCreateBaermann, useUpdateBaermann } from '../hooks/useBaermann';
import type { PoolLocal } from './BaermannPoolCard';
import { BaermannPoolsSection } from './BaermannPoolsSection';

type TabId = 'datos' | 'pools';

const datosSchema = z.object({
  fecha_analisis: z.date({ message: 'Fecha de análisis inválida' }),
  identificacion_muestras: z.string().nullable(),
  alimentacion: z.string().nullable(),
  ultimo_antiparasitario: z.string().nullable(),
  fecha_ultimo_tratamiento: z.date().nullable(),
  comentarios_lote: z.string().nullable(),
});

type DatosValues = z.infer<typeof datosSchema>;

const DATOS_FIELDS: (keyof DatosValues)[] = [
  'fecha_analisis',
  'identificacion_muestras',
  'alimentacion',
  'ultimo_antiparasitario',
  'fecha_ultimo_tratamiento',
  'comentarios_lote',
];

const DEFAULT_DATOS: DatosValues = {
  fecha_analisis: new Date(),
  identificacion_muestras: null,
  alimentacion: null,
  ultimo_antiparasitario: null,
  fecha_ultimo_tratamiento: null,
  comentarios_lote: null,
};

type BaermannDraftData = {
  datosValues: Omit<DatosValues, 'fecha_analisis' | 'fecha_ultimo_tratamiento'> & {
    fecha_analisis: string;
    fecha_ultimo_tratamiento: string | null;
  };
  pools: PoolLocal[];
};

function makeEmptyPool(): PoolLocal {
  return { id: randomUUID(), numeros_muestras: [], valor: null, observacion: null };
}

function validatePools(pools: PoolLocal[]): string | null {
  if (pools.some((p) => p.valor === null)) return 'Hay pools sin resultado.';
  return null;
}

interface BaermannFormDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  uidAnalisis?: string;
  numeroProtocolo?: number;
  establecimientoNombre?: string;
  onSuccess?: () => void;
}

export function BaermannFormDialog({
  open,
  onClose,
  uidProtocolo,
  uidAnalisis,
  numeroProtocolo,
  establecimientoNombre,
  onSuccess,
}: BaermannFormDialogProps): JSX.Element {
  const isEdit = !!uidAnalisis;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();

  const baermannQuery = useBaermann(uidProtocolo, uidAnalisis);
  const createMutation = useCreateBaermann();
  const updateMutation = useUpdateBaermann();

  const [tabActiva, setTabActiva] = useState<TabId>('datos');
  const [pools, setPools] = useState<PoolLocal[]>(() => [makeEmptyPool()]);
  const [poolsDirty, setPoolsDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pendingDraft, setPendingDraft] = useState<AnalisisDraft<BaermannDraftData> | null>(null);

  const { saveDraft, loadDraft, clearDraft } = useAnalisisDraft<BaermannDraftData>('baermann');

  const methods = useForm<DatosValues>({
    resolver: zodResolver(datosSchema),
    defaultValues: { ...DEFAULT_DATOS, fecha_analisis: new Date() },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, errors, submitCount },
  } = methods;

  const watchedAll = useWatch({ control });

  useEffect(() => {
    if (open && !uidAnalisis) {
      const d = loadDraft(uidProtocolo);
      setPendingDraft(d);
      reset({ ...DEFAULT_DATOS, fecha_analisis: new Date() });
      setPools([makeEmptyPool()]);
      setPoolsDirty(false);
      setTabActiva('datos');
      setShowErrors(false);
      setSubmitError(null);
    }
  }, [open, uidAnalisis, uidProtocolo, reset, loadDraft]);

  // Auto-save draft in create mode
  useEffect(() => {
    if (!open || isEdit) return;
    saveDraft(uidProtocolo, {
      datosValues: {
        ...watchedAll,
        fecha_analisis: watchedAll.fecha_analisis?.toISOString() ?? '',
        fecha_ultimo_tratamiento: watchedAll.fecha_ultimo_tratamiento?.toISOString() ?? null,
      },
      pools,
    });
  }, [watchedAll, pools, open, isEdit, saveDraft, uidProtocolo]);

  useEffect(() => {
    if (isEdit && baermannQuery.data) {
      const b = baermannQuery.data;
      reset({
        fecha_analisis: parseISO(b.fecha_analisis),
        identificacion_muestras: b.identificacion_muestras,
        alimentacion: b.alimentacion,
        ultimo_antiparasitario: b.ultimo_antiparasitario,
        fecha_ultimo_tratamiento: b.fecha_ultimo_tratamiento
          ? parseISO(b.fecha_ultimo_tratamiento)
          : null,
        comentarios_lote: b.comentarios_lote,
      });
      const loadedPools = b.pools.map((p) => ({
        id: randomUUID(),
        numeros_muestras: p.numeros_muestras,
        valor: p.valor,
        observacion: p.observacion,
      }));
      setPools(loadedPools.length > 0 ? loadedPools : [makeEmptyPool()]);
      setPoolsDirty(false);
      setTabActiva('datos');
      setShowErrors(false);
      setSubmitError(null);
    }
  }, [isEdit, baermannQuery.data, reset]);

  // Warn before tab close when there are unsaved changes
  useEffect(() => {
    if (!isDirty && !poolsDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, poolsDirty]);

  const isPending = createMutation.isPending || updateMutation.isPending;
  const hasAttemptedSubmit = submitCount > 0;
  const tabDatosHasError = hasAttemptedSubmit && DATOS_FIELDS.some((f) => !!errors[f]);
  const tabPoolsHasError = showErrors && validatePools(pools) !== null;

  function handlePoolsChange(updated: PoolLocal[]): void {
    setPools(updated);
    setPoolsDirty(true);
  }

  function handleClose(): void {
    if (isDirty || poolsDirty) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }

  function handleConfirmDiscard(): void {
    setShowDiscardDialog(false);
    onClose();
  }

  function handleRestoreDraft(): void {
    if (!pendingDraft) return;
    const d = pendingDraft.data;
    reset({
      ...d.datosValues,
      fecha_analisis: parseISO(d.datosValues.fecha_analisis),
      fecha_ultimo_tratamiento: d.datosValues.fecha_ultimo_tratamiento
        ? parseISO(d.datosValues.fecha_ultimo_tratamiento)
        : null,
    });
    setPools(d.pools.length > 0 ? d.pools : [makeEmptyPool()]);
    setTabActiva('datos');
    setPendingDraft(null);
  }

  function handleDiscardDraft(): void {
    clearDraft(uidProtocolo);
    setPendingDraft(null);
  }

  function handleSaveError(errs: FieldErrors<DatosValues>): void {
    if (DATOS_FIELDS.some((f) => !!errs[f])) setTabActiva('datos');
  }

  async function handleSave(values: DatosValues): Promise<void> {
    setSubmitError(null);

    const poolsError = validatePools(pools);
    if (poolsError) {
      setShowErrors(true);
      setSubmitError(new Error(poolsError));
      setTabActiva('pools');
      return;
    }

    const mappedPools = pools.map((p) => ({
      numeros_muestras: p.numeros_muestras,
      valor: p.valor!,
      observacion: p.observacion,
    }));

    try {
      if (isEdit && uidAnalisis) {
        await updateMutation.mutateAsync({
          uidProtocolo,
          uidAnalisis,
          data: {
            fecha_analisis: values.fecha_analisis.toISOString(),
            identificacion_muestras: values.identificacion_muestras,
            alimentacion: values.alimentacion,
            ultimo_antiparasitario: values.ultimo_antiparasitario,
            fecha_ultimo_tratamiento: values.fecha_ultimo_tratamiento?.toISOString() ?? null,
            comentarios_lote: values.comentarios_lote,
            pools: mappedPools,
            uid_analisis_hpg_referencia: null,
          },
        });
        notify.success('Análisis Baermann actualizado.');
      } else {
        await createMutation.mutateAsync({
          uidProtocolo,
          data: {
            tipo_analisis: TipoAnalisis.BAERMANN,
            fecha_analisis: values.fecha_analisis.toISOString(),
            identificacion_muestras: values.identificacion_muestras,
            alimentacion: values.alimentacion,
            ultimo_antiparasitario: values.ultimo_antiparasitario,
            fecha_ultimo_tratamiento: values.fecha_ultimo_tratamiento?.toISOString() ?? null,
            comentarios_lote: values.comentarios_lote,
            pools: mappedPools,
            uid_analisis_hpg_referencia: null,
          },
        });
        notify.success('Análisis Baermann creado.');
        clearDraft(uidProtocolo);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      if (isApiError(err) && err.isConflict()) {
        setShowLockedDialog(true);
      } else {
        setSubmitError(err);
      }
    }
  }

  const subtitleParts = [
    numeroProtocolo != null ? `Protocolo N° ${numeroProtocolo}` : null,
    establecimientoNombre ?? null,
  ].filter(Boolean);
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;

  const dialogTitle = (() => {
    if (!isEdit) return 'Nuevo análisis Baermann';
    const numFmt =
      numeroProtocolo != null
        ? formatNumeroAnalisis(numeroProtocolo, baermannQuery.data?.numero_analisis ?? null)
        : null;
    return numFmt ? `Editar análisis Baermann ${numFmt}` : 'Editar análisis Baermann';
  })();

  const isLoadingContent = isEdit && baermannQuery.isLoading;
  const isErrorContent = isEdit && baermannQuery.isError;
  const showForm = !isLoadingContent && !isErrorContent;

  const datosLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Datos del análisis</span>
      {tabDatosHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  const poolsLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Pools</span>
      {tabPoolsHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        fullScreen={isSmall}
        onClose={isPending ? undefined : handleClose}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: subtitle ? 1 : 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{dialogTitle}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            aria-label="Cerrar"
            size="small"
            sx={{ mt: 0.5 }}
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {showForm && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabActiva} onChange={(_, v: TabId) => setTabActiva(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: 3 }}>
              <Tab value="datos" label={datosLabel} />
              <Tab value="pools" label={poolsLabel} />
            </Tabs>
          </Box>
        )}

        {showForm && pendingDraft && (
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(90, 28, 112, 0.10)',
                color: '#3d1350',
                '& .MuiAlert-icon': { color: '#5A1C70' },
              }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={handleRestoreDraft}>
                    Restaurar
                  </Button>
                  <Button size="small" color="inherit" onClick={handleDiscardDraft}>
                    Descartar
                  </Button>
                </Box>
              }
            >
              Tenés un borrador guardado{' '}
              {formatDistanceToNow(new Date(pendingDraft.timestamp), {
                addSuffix: true,
                locale: es,
              })}.
            </Alert>
          </Box>
        )}

        <DialogContent dividers sx={{ px: 3, py: 1 }}>
          {isLoadingContent && (
            <Box>
              <Skeleton variant="rounded" height={200} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={320} />
            </Box>
          )}

          {isErrorContent && (
            <Alert severity="error">{getErrorMessage(baermannQuery.error)}</Alert>
          )}

          {showForm && (
            <>
              {submitError != null && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {String(getErrorMessage(submitError))}
                </Alert>
              )}

              {tabActiva === 'datos' && <AnalisisDatosGeneralesForm noPaper />}

              {tabActiva === 'pools' && (
                <BaermannPoolsSection
                  pools={pools}
                  onChange={handlePoolsChange}
                  showErrors={showErrors}
                  disabled={isPending}
                  hideMuestras
                />
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={isPending || (isEdit && !isDirty && !poolsDirty)}
            onClick={() => void handleSubmit(handleSave, handleSaveError)()}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isPending ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDiscardDialog} maxWidth="xs" onClose={() => setShowDiscardDialog(false)}>
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>¿Descartar los cambios sin guardar?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Seguir editando</Button>
          <Button color="error" onClick={handleConfirmDiscard}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLockedDialog} maxWidth="xs">
        <DialogTitle>Protocolo cerrado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Este protocolo está cerrado y ya no admite cambios.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowLockedDialog(false);
              onClose();
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
